import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../../axiosConfig';
import {
  AccelerationHistoryChart,
  BoreholeLogChart,
  CSRCRRProfileChart,
  FLProfileChart,
  soilColor,
} from './seismicCharts';
import LPIGauge from './LPIGauge';
import ResponseSpectrumPanel from './ResponseSpectrumPanel';

/**
 * 지반응답해석 상세 (시추공 1 × 지진파 1)
 *
 * 배치 해석(FL/LPI 목록)에서 시추공 하나를 선택했을 때 들어오는 심층 화면이다.
 * 입력 조건과 해석 결과를 두 개의 탭으로 나눠 보여준다.
 *
 * 백엔드의 POST /seismic/case-analysis 를 호출한다. 해석 엔진은 2026년 과업에서
 * 백엔드에 통합된 v3(순수 Python SHAKE)이며, 단일 케이스는 1초 안에 끝난다.
 */
const GroundResponseDetail = ({ boreholeId, boreholeName, onBack }) => {
  const [options, setOptions] = useState(null);
  const [earthquakeIndex, setEarthquakeIndex] = useState(0);
  const [materialSet, setMaterialSet] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('input');

  // 선택지(설계지진파 21종, 동적물성 세트) 로드
  useEffect(() => {
    let alive = true;
    axios
      .get('/seismic/options')
      .then((res) => {
        if (!alive) return;
        const data = res.data.data;
        setOptions(data);
        setMaterialSet(data.active_material_set || data.material_sets?.[0] || '');
      })
      .catch(() => alive && setError('해석 선택지를 불러오지 못했습니다.'));
    return () => {
      alive = false;
    };
  }, []);

  const runAnalysis = useCallback(async () => {
    if (!boreholeId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post('/seismic/case-analysis', {
        borehole_id: boreholeId,
        earthquake_index: earthquakeIndex,
        material_set: materialSet || null,
      });
      setResult(res.data.data);
      setTab('result');
    } catch (e) {
      const detail = e?.response?.data?.message || e?.response?.data?.detail;
      setError(detail || '해석에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [boreholeId, earthquakeIndex, materialSet]);

  const selectedEq = useMemo(
    () => options?.earthquakes?.find((e) => e.index === earthquakeIndex),
    [options, earthquakeIndex]
  );

  const summary = result?.summary;

  return (
    <div className="bg-white rounded-lg shadow-soft">
      {/* ── 헤더 ─────────────────────────────────────────── */}
      <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-center gap-4">
        {onBack && (
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-800 text-sm font-medium"
          >
            ← 목록
          </button>
        )}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded bg-primary-600 text-white text-xs font-bold">
            LQ
          </span>
          <h2 className="text-lg font-bold text-gray-800">지반응답해석 상세</h2>
        </div>

        <div className="flex items-center gap-3 ml-auto flex-wrap">
          <label className="text-xs text-gray-500">
            시추공
            <div className="text-sm font-semibold text-gray-800">
              {result?.borehole || boreholeName || '-'}
            </div>
          </label>

          <label className="text-xs text-gray-500">
            동적물성 세트
            <select
              value={materialSet}
              onChange={(e) => setMaterialSet(e.target.value)}
              className="block mt-0.5 border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {options?.material_sets?.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs text-gray-500">
            설계지진파
            <select
              value={earthquakeIndex}
              onChange={(e) => setEarthquakeIndex(Number(e.target.value))}
              className="block mt-0.5 border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {options?.earthquakes?.map((eq) => (
                <option key={eq.index} value={eq.index}>
                  {eq.waveform} · 재현주기 {eq.return_period}년
                </option>
              ))}
            </select>
          </label>

          <button
            onClick={runAnalysis}
            disabled={loading || !boreholeId}
            className="px-4 py-2 rounded bg-primary-600 text-white text-sm font-semibold
                       hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? '해석 중…' : '▶ 응답해석 시작'}
          </button>
        </div>
      </div>

      {/* ── 탭 ───────────────────────────────────────────── */}
      <div className="px-6 pt-3 border-b border-gray-200 flex gap-1">
        {[
          { key: 'input', label: '입력 및 설정' },
          { key: 'result', label: '응답해석 결과' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            disabled={t.key === 'result' && !result}
            className={`px-4 py-2 text-sm font-medium rounded-t border-b-2 -mb-px transition
              ${
                tab === t.key
                  ? 'border-primary-600 text-primary-700 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700 disabled:text-gray-300'
              }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mx-6 mt-4 px-4 py-3 rounded bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ── 입력 및 설정 ──────────────────────────────────── */}
      {tab === 'input' && (
        <div className="p-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
          <section className="border border-gray-200 rounded-lg">
            <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-700">시추주상도</span>
              <span className="text-xs text-gray-400">
                {result ? `0.0 – ${summary.total_depth} m` : '해석 전'}
              </span>
            </div>
            <div className="p-3">
              {result ? (
                <BoreholeLogChart layers={result.layers} waterLevel={result.water_level} />
              ) : (
                <EmptyHint text="해석을 실행하면 지층 구성이 표시됩니다." />
              )}
            </div>
            {result && (
              <div className="grid grid-cols-2 gap-2 px-3 pb-3">
                <MiniStat label="지하수위" value={`${result.water_level} m`} />
                <MiniStat label="층 개수" value={`${summary.layer_count} 개`} />
              </div>
            )}
          </section>

          <section className="border border-gray-200 rounded-lg">
            <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-700">지층별 물성</span>
              <span className="text-xs text-gray-400">
                토질 / 단위중량 / FC / SPT-N / Vs
              </span>
            </div>
            <div className="overflow-x-auto">
              {result ? (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      {['층', '상단심도', '하단심도', '토질', '단위중량', 'FC', 'SPT-N', 'Vs'].map(
                        (h) => (
                          <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {result.layers.map((l, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                        <td className="px-3 py-2 tabular-nums">{l.top_depth.toFixed(1)}</td>
                        <td className="px-3 py-2 tabular-nums">{l.bottom_depth.toFixed(1)}</td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              className="inline-block w-2.5 h-2.5 rounded-sm"
                              style={{ background: soilColor(l.soil) }}
                            />
                            {l.soil}
                          </span>
                        </td>
                        <td className="px-3 py-2 tabular-nums">{l.density.toFixed(2)}</td>
                        <td className="px-3 py-2 tabular-nums">{l.fc.toFixed(1)}</td>
                        <td className="px-3 py-2 tabular-nums">{l.spt_n.toFixed(1)}</td>
                        <td className="px-3 py-2 tabular-nums">{l.vs}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6">
                  <EmptyHint text="시추공과 설계지진파를 고른 뒤 [응답해석 시작]을 누르세요." />
                </div>
              )}
            </div>
          </section>

          <section className="lg:col-span-2 border border-gray-200 rounded-lg">
            <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-700">설계지진파</span>
              <span className="text-xs text-gray-400">
                7개 파형 × 재현주기 500 / 1,000 / 2,400년 = 21종
              </span>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniStat label="파형" value={selectedEq?.waveform || '-'} />
              <MiniStat
                label="재현주기"
                value={selectedEq ? `${selectedEq.return_period} 년` : '-'}
              />
              <MiniStat label="dt" value={selectedEq ? `${selectedEq.dt} s` : '-'} />
              <MiniStat label="length" value={selectedEq ? selectedEq.length.toLocaleString() : '-'} />
            </div>
          </section>
        </div>
      )}

      {/* ── 응답해석 결과 ─────────────────────────────────── */}
      {tab === 'result' && result && (
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-6">
            <section className="border border-gray-200 rounded-lg">
              <div className="px-4 py-2.5 border-b border-gray-200">
                <span className="text-sm font-semibold text-gray-700">
                  입력 / 지표면 가속도 시간이력
                </span>
              </div>
              <div className="p-3">
                <AccelerationHistoryChart timeHistory={result.time_history} />
              </div>
            </section>

            <section className="border border-gray-200 rounded-lg">
              <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700">액상화위험지수 LPI</span>
                <span className="text-xs text-gray-400">
                  {result.borehole} / {result.earthquake.waveform}
                </span>
              </div>
              <div className="p-4">
                <LPIGauge value={result.lpi.value} level={result.lpi.level} />
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <MiniStat label="입력 PGA" value={`${summary.input_pga} g`} />
                  <MiniStat label="지표면 PGA" value={`${summary.surface_pga} g`} />
                  <MiniStat label="증폭비" value={summary.amplification ? `${summary.amplification} ×` : '-'} />
                  <MiniStat label="최소 FL" value={summary.min_fl ?? '-'} />
                  <MiniStat
                    label="주요 기여 심도"
                    value={summary.critical_depth != null ? `${summary.critical_depth} m` : '해당 없음'}
                  />
                  <MiniStat label="지하수위" value={`${result.water_level} m`} />
                  <MiniStat
                    label="Strain error"
                    value={summary.strain_error ? '주의' : '정상'}
                    tone={summary.strain_error ? 'warn' : 'ok'}
                  />
                  <MiniStat label="동적물성" value={result.material_set || '-'} />
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-gray-500">
                  산정식: {result.lpi.method}
                </p>
              </div>
            </section>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section className="border border-gray-200 rounded-lg">
              <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700">심도별 액상화안전율 FL</span>
                <span className="text-xs text-gray-400">FL = 1.0 기준선</span>
              </div>
              <div className="p-3">
                <FLProfileChart profile={result.profile} />
              </div>
            </section>

            <section className="border border-gray-200 rounded-lg">
              <div className="px-4 py-2.5 border-b border-gray-200">
                <span className="text-sm font-semibold text-gray-700">심도별 CSR / CRR</span>
              </div>
              <div className="p-3">
                <CSRCRRProfileChart profile={result.profile} />
              </div>
            </section>
          </div>

          {/* AI 예측 응답스펙트럼 — 수치해석 결과와 나란히 확인한다 */}
          <ResponseSpectrumPanel
            boreholeId={boreholeId}
            earthquakeIndex={earthquakeIndex}
            materialSet={materialSet}
            available={options?.response_spectrum_available}
          />

          <section className="border border-gray-200 rounded-lg">
            <div className="px-4 py-2.5 border-b border-gray-200 flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-700">심도별 해석 결과</span>
              <span className="text-xs text-gray-400">{result.profile.length}개 층</span>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 sticky top-0">
                  <tr>
                    {['심도', '토질', 'FL', 'CSR', 'CRR', '최대가속도', 'σ′v'].map((h) => (
                      <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.profile.map((r, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-3 py-1.5 tabular-nums">{r.depth.toFixed(2)} m</td>
                      <td className="px-3 py-1.5">{r.soil}</td>
                      <td
                        className={`px-3 py-1.5 tabular-nums font-medium ${
                          r.fl < 1 ? 'text-red-600' : 'text-gray-700'
                        }`}
                      >
                        {r.fl.toFixed(2)}
                      </td>
                      <td className="px-3 py-1.5 tabular-nums">{r.csr.toFixed(3)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{r.crr.toFixed(3)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{r.max_acc.toFixed(3)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{r.sigma_v.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

const MiniStat = ({ label, value, tone }) => (
  <div className="border border-gray-200 rounded px-3 py-2">
    <div className="text-[11px] text-gray-500">{label}</div>
    <div
      className={`text-sm font-semibold ${
        tone === 'warn' ? 'text-amber-600' : tone === 'ok' ? 'text-green-600' : 'text-gray-800'
      }`}
    >
      {value}
    </div>
  </div>
);

const EmptyHint = ({ text }) => (
  <div className="py-10 text-center text-sm text-gray-400">{text}</div>
);

export default GroundResponseDetail;
